import React, { Dispatch, RefObject, useCallback } from 'react';

import globalWindowSize from '@/utils/global-window-size';
import { PopoverPositionOptions } from '../popover.types';

type Spaces = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

type Rects = {
  trigger: DOMRect;
  content: DOMRect;
  container: DOMRect;
  window: { width: number; height: number };
};

type Offsets = {
  vertical: number;
  horizontal: number;
};

type PositionResult = {
  top: string;
  left: string;
  right: string;
  bottom: string;
  slideFrom?: 'top' | 'bottom';
};

// Position configuration with fit-checking and offset calculation
type PositionConfig = {
  vertical?: 'top' | 'bottom';
  horizontal?: 'left' | 'right' | 'center' | 'center-left' | 'center-right';
  checkFits: (rects: Rects, offsets: Offsets) => { vertical: boolean; horizontal: boolean };
  getStyles: (rects: Rects, offsets: Offsets) => PositionResult;
};

const POSITION_CONFIGS: Record<PopoverPositionOptions, PositionConfig> = {
  'top-left': {
    vertical: 'top',
    horizontal: 'left',
    checkFits: ({ trigger, content }, { vertical, horizontal }) => ({
      vertical: trigger.top - content.height + vertical >= 0,
      horizontal: trigger.right - content.width - horizontal >= 0,
    }),
    getStyles: ({ trigger, content }, { vertical, horizontal }) => ({
      top: `-${content.height - vertical}px`,
      left: `-${content.width - trigger.width + horizontal + 4}px`,
      right: '0',
      bottom: '0',
      slideFrom: 'top',
    }),
  },
  'top-center': {
    vertical: 'top',
    horizontal: 'center',
    checkFits: ({ trigger, content, window }, { vertical, horizontal }) => {
      const centerLeft = trigger.left + (trigger.width - content.width) / 2 + horizontal;
      return {
        vertical: trigger.top - content.height + vertical >= 0,
        horizontal: centerLeft >= 0 && centerLeft + content.width <= window.width,
      };
    },
    getStyles: ({ trigger, content }, { vertical, horizontal }) => ({
      top: `-${content.height - vertical}px`,
      left: `${(trigger.width - content.width) / 2 + horizontal}px`,
      right: '0',
      bottom: '0',
      slideFrom: 'top',
    }),
  },
  'top-right': {
    vertical: 'top',
    horizontal: 'right',
    checkFits: ({ trigger, content, window }, { vertical, horizontal }) => ({
      vertical: trigger.top - content.height + vertical >= 0,
      horizontal: trigger.left + content.width + horizontal <= window.width,
    }),
    getStyles: ({ content, container }, { vertical, horizontal }) => ({
      top: `-${content.height - vertical}px`,
      left: '0',
      right: `${content.width - container.width + horizontal}px`,
      bottom: '0',
      slideFrom: 'top',
    }),
  },
  'bottom-left': {
    vertical: 'bottom',
    horizontal: 'left',
    checkFits: ({ trigger, content, window }, { vertical, horizontal }) => ({
      vertical: trigger.bottom + content.height + vertical <= window.height,
      horizontal: trigger.right - content.width - horizontal >= 0,
    }),
    getStyles: ({ trigger, content }, { vertical, horizontal }) => ({
      top: `${trigger.height + vertical}px`,
      left: `-${content.width - trigger.width + horizontal + 4}px`,
      right: '0',
      bottom: '0',
      slideFrom: 'bottom',
    }),
  },
  'bottom-center': {
    vertical: 'bottom',
    horizontal: 'center',
    checkFits: ({ trigger, content, window }, { vertical, horizontal }) => {
      const centerLeft = trigger.left + (trigger.width - content.width) / 2 + horizontal;
      return {
        vertical: trigger.bottom + content.height + vertical <= window.height,
        horizontal: centerLeft >= 0 && centerLeft + content.width <= window.width,
      };
    },
    getStyles: ({ trigger, content }, { vertical, horizontal }) => ({
      top: `${trigger.height + vertical}px`,
      left: `${(trigger.width - content.width) / 2 + horizontal}px`,
      right: '0',
      bottom: '0',
      slideFrom: 'bottom',
    }),
  },
  'bottom-right': {
    vertical: 'bottom',
    horizontal: 'right',
    checkFits: ({ trigger, content, window }, { vertical, horizontal }) => ({
      vertical: trigger.bottom + content.height + vertical <= window.height,
      horizontal: trigger.left + content.width + horizontal <= window.width,
    }),
    getStyles: ({ trigger, content, container }, { vertical, horizontal }) => ({
      top: `${trigger.height + vertical}px`,
      left: '0',
      right: `${content.width - container.width + horizontal}px`,
      bottom: '0',
      slideFrom: 'bottom',
    }),
  },
  'center-left': {
    horizontal: 'center-left',
    checkFits: ({ trigger, content, window }, { vertical, horizontal }) => {
      const wouldFitBottom = trigger.bottom + content.height / 2 + vertical <= window.height;
      const wouldFitTop = trigger.top - content.height / 2 + vertical >= 0;
      return {
        vertical: wouldFitBottom || wouldFitTop,
        horizontal: trigger.left - content.width - horizontal - 8 >= 0,
      };
    },
    getStyles: ({ trigger, content, window }, { vertical, horizontal }) => {
      let topOffset = (content.height + vertical) / 2 - trigger.height / 2;
      const centeredTop = trigger.top - topOffset;
      const centeredBottom = centeredTop + content.height;

      if (centeredTop < 0) {
        topOffset = trigger.top - vertical;
      } else if (centeredBottom > window.height) {
        topOffset = trigger.top - (window.height - content.height) + vertical;
      }

      return {
        top: `-${topOffset}px`,
        left: `-${content.width + horizontal + 8}px`,
        right: '0',
        bottom: '0',
      };
    },
  },
  'center-right': {
    horizontal: 'center-right',
    checkFits: ({ trigger, content, window }, { vertical, horizontal }) => {
      const wouldFitBottom = trigger.bottom + content.height / 2 + vertical <= window.height;
      const wouldFitTop = trigger.top - content.height / 2 + vertical >= 0;
      return {
        vertical: wouldFitBottom || wouldFitTop,
        horizontal: trigger.right + content.width + horizontal + 8 <= window.width,
      };
    },
    getStyles: ({ trigger, content, window }, { vertical, horizontal }) => {
      let topOffset = (content.height + vertical) / 2 - trigger.height / 2;
      const centeredTop = trigger.top - topOffset;
      const centeredBottom = centeredTop + content.height;

      if (centeredTop < 0) {
        topOffset = trigger.top - vertical;
      } else if (centeredBottom > window.height) {
        topOffset = trigger.top - (window.height - content.height) + vertical;
      }

      return {
        top: `-${topOffset}px`,
        left: `${trigger.width + horizontal + 8}px`,
        right: '0',
        bottom: '0',
      };
    },
  },
};

// Calculate available space in each direction
const calculateSpaces = (trigger: DOMRect, window: { width: number; height: number }): Spaces => ({
  top: trigger.top,
  bottom: window.height - trigger.bottom,
  left: trigger.left,
  right: window.width - trigger.right,
});

// Determine the best fallback position based on fit results
const determineFallbackPosition = (
  preferred: PopoverPositionOptions,
  verticalFits: boolean,
  horizontalFits: boolean,
  spaces: Spaces
): PopoverPositionOptions => {
  const config = POSITION_CONFIGS[preferred];
  const { vertical: preferredVertical, horizontal: preferredHorizontal } = config;

  if (verticalFits && horizontalFits) {
    return preferred;
  }

  // Helper to pick vertical direction based on available space
  const pickVertical = () => (spaces.bottom > spaces.top ? 'bottom' : 'top');

  // Helper to pick horizontal direction based on available space
  const pickHorizontal = () => (spaces.right > spaces.left ? 'right' : 'left');

  // Both don't fit - flip both
  if (!verticalFits && !horizontalFits) {
    if (preferredHorizontal === 'center-left') {
      return `${pickVertical()}-right` as PopoverPositionOptions;
    }
    if (preferredHorizontal === 'center-right') {
      return `${pickVertical()}-left` as PopoverPositionOptions;
    }
    const newVertical = preferredVertical === 'top' ? 'bottom' : 'top';
    const newHorizontal =
      preferredHorizontal === 'left' ? 'right' : preferredHorizontal === 'right' ? 'left' : pickHorizontal();
    return `${newVertical}-${newHorizontal}` as PopoverPositionOptions;
  }

  // Vertical fits, horizontal doesn't - flip horizontal
  if (verticalFits && !horizontalFits) {
    if (preferredHorizontal === 'center-left') {
      return `${pickVertical()}-right` as PopoverPositionOptions;
    }
    if (preferredHorizontal === 'center-right') {
      return `${pickVertical()}-left` as PopoverPositionOptions;
    }
    const newHorizontal =
      preferredHorizontal === 'left' ? 'right' : preferredHorizontal === 'right' ? 'left' : pickHorizontal();
    return `${preferredVertical}-${newHorizontal}` as PopoverPositionOptions;
  }

  // Horizontal fits, vertical doesn't - flip vertical
  if (!verticalFits && horizontalFits) {
    if (preferredHorizontal === 'center-left' || preferredHorizontal === 'center-right') {
      const newVertical = pickVertical();
      const newHorizontal = preferredHorizontal === 'center-left' ? 'left' : 'right';
      return `${newVertical}-${newHorizontal}` as PopoverPositionOptions;
    }
    const newVertical = preferredVertical === 'top' ? 'bottom' : 'top';
    return `${newVertical}-${preferredHorizontal}` as PopoverPositionOptions;
  }

  return preferred;
};

// Select the best position based on preferences and available space
const selectPosition = (
  forcePosition: PopoverPositionOptions | undefined,
  preferredPosition: PopoverPositionOptions | undefined,
  rects: Rects,
  offsets: Offsets
): PopoverPositionOptions | undefined => {
  if (forcePosition) {
    return forcePosition;
  }

  if (preferredPosition) {
    const config = POSITION_CONFIGS[preferredPosition];
    const { vertical: verticalFits, horizontal: horizontalFits } = config.checkFits(rects, offsets);
    const spaces = calculateSpaces(rects.trigger, rects.window);
    return determineFallbackPosition(preferredPosition, verticalFits, horizontalFits, spaces);
  }

  return undefined;
};

// Apply default positioning when no position is specified
const applyDefaultPosition = (
  containerEl: HTMLElement,
  triggerEl: HTMLElement,
  rects: Rects,
  offsets: Offsets,
  display: string,
  open: boolean,
  setSlideFrom: Dispatch<React.SetStateAction<'top' | 'bottom' | undefined>>
) => {
  const { trigger, content, window } = rects;
  const { vertical, horizontal } = offsets;

  let movedHorizontal = false;

  // Check horizontal positioning
  if (content.right - content.width / 2 + trigger.width / 2 > window.width) {
    // Move left
    containerEl.style.left = `-${content.width - triggerEl.offsetWidth + horizontal + 4}px`;
    movedHorizontal = true;
  } else if (content.left - content.width / 2 + trigger.width / 2 < 0) {
    // Move right
    containerEl.style.left = horizontal ? `-${horizontal}px` : '0';
    movedHorizontal = true;
  }

  // Check vertical positioning
  if (content.bottom + trigger.height > window.height) {
    // Move up
    containerEl.style.top = `-${content.height - vertical}px`;
    if (display === 'none' && open) {
      setSlideFrom('top');
    }
  } else {
    // Move down
    containerEl.style.top = `${trigger.height + vertical}px`;
    if (display === 'none' && open) {
      setSlideFrom('bottom');
    }
  }

  // Center horizontally if not moved
  if (!movedHorizontal) {
    const halfTriggerWidth = trigger.width / 2;
    containerEl.style.left = `-${content.width / 2 - halfTriggerWidth + horizontal}px`;
  }
};

type Props = {
  contentContainerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLButtonElement | null>;
  portalEl?: RefObject<HTMLDivElement | null>;
  horizontalOffset?: number;
  verticalOffset?: number;
  forcePosition?: PopoverPositionOptions;
  preferredPosition?: PopoverPositionOptions;
  display: string;
  open: boolean;
  setSlideFrom: Dispatch<React.SetStateAction<'top' | 'bottom' | undefined>>;
};

const useCalculatePosition = ({
  display,
  contentContainerRef,
  contentRef,
  triggerRef,
  setSlideFrom,
  open,
  portalEl,
  verticalOffset = 0,
  horizontalOffset = 0,
  forcePosition,
  preferredPosition,
}: Props) => {
  return useCallback(() => {
    const containerEl = contentContainerRef.current;
    const contentEl = contentRef.current;
    const triggerEl = triggerRef.current;
    if (!containerEl || !contentEl || !triggerEl) {
      return;
    }

    // Reset position
    const rect = containerEl.getBoundingClientRect();
    const x = portalEl ? rect.x || 0 : 0;
    const y = portalEl ? rect.y || 0 : 0;
    containerEl.style.top = verticalOffset ? `${y - verticalOffset}px` : `${y}px`;
    containerEl.style.left = horizontalOffset ? `${x - horizontalOffset}px` : `${x}px`;
    containerEl.style.right = '0';
    containerEl.style.bottom = '0';

    const rects: Rects = {
      content: contentEl.getBoundingClientRect(),
      trigger: triggerEl.getBoundingClientRect(),
      container: containerEl.getBoundingClientRect(),
      window: { width: globalWindowSize.innerWidth, height: globalWindowSize.innerHeight },
    };

    const offsets: Offsets = {
      vertical: verticalOffset,
      horizontal: horizontalOffset,
    };

    const selectedPosition = selectPosition(forcePosition, preferredPosition, rects, offsets);

    if (selectedPosition) {
      const config = POSITION_CONFIGS[selectedPosition];
      const styles = config.getStyles(rects, offsets);

      containerEl.style.top = styles.top;
      containerEl.style.left = styles.left;
      containerEl.style.right = styles.right;
      containerEl.style.bottom = styles.bottom;

      if (styles.slideFrom && display === 'none' && open) {
        setSlideFrom(styles.slideFrom);
      }
    } else {
      // No position specified - use default behavior
      applyDefaultPosition(containerEl, triggerEl, rects, offsets, display, open, setSlideFrom);
    }
  }, [
    contentContainerRef,
    contentRef,
    triggerRef,
    portalEl,
    forcePosition,
    preferredPosition,
    verticalOffset,
    horizontalOffset,
    display,
    open,
    setSlideFrom,
  ]);
};

export default useCalculatePosition;
